// src/components/DashboardContent.js - Fixed for banner persistence
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import IconSelector from './IconSelector';
import LinksManager from './LinksManager';
import PaletteSelector from './PaletteSelector';
import ProfilePreview from './ProfilePreview';
import DetailsEditor from './DetailsEditor';
import GhostMode from './GhostMode';
import LinkShortener from './LinkShortener';
import ThemeSelector from './ThemeSelector';
import CancelAccount from './CancelAccount';
import ReportProblem from './ReportProblem';
import SubscriptionInfo from './SubscriptionInfo';
import WelcomePage from './WelcomePage';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import styles from './Dashboard.module.css';

const DashboardContent = ({
  activeSection,
  user,
  userData,
  setUserData,
  setActiveSection,
  isProUser,
  onUpgradeClick
}) => {
  // State for live preview of text color changes
  const [previewTextColor, setPreviewTextColor] = useState(userData.profile?.textColor || '#444444');
  
  // Add flag to track when we're in the middle of a color change
  const [isUpdatingColor, setIsUpdatingColor] = useState(false);
  
  // Use location to check if we should activate a specific section (used for subscription management)
  const location = useLocation();

  // Set the active section from location state if provided
  useEffect(() => {
    if (location.state && location.state.activeSection) {
      setActiveSection(location.state.activeSection);
    }
  }, [location.state, setActiveSection]);

  // Function to get the darkest color from the palette for pro users
  const getDarkestColor = () => {
    const colors = userData.profile?.palette?.colors;
    if (!colors || colors.length === 0) return '#000000';
    
    return colors.reduce((darkest, current) => {
      const hex = current.replace('#', '');
      const r = parseInt(hex.slice(0, 2), 16) / 255;
      const g = parseInt(hex.slice(2, 4), 16) / 255;
      const b = parseInt(hex.slice(4, 6), 16) / 255;
      
      const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      
      const darkestHex = darkest.replace('#', '');
      const dr = parseInt(darkestHex.slice(0, 2), 16) / 255;
      const dg = parseInt(darkestHex.slice(2, 4), 16) / 255;
      const db = parseInt(darkestHex.slice(4, 6), 16) / 255;
      const darkestLuminance = 0.2126 * dr + 0.7152 * dg + 0.0722 * db;
      
      return luminance < darkestLuminance ? current : darkest;
    }, colors[0]);
  };

  // Function to handle details updates with database save
  const handleDetailsUpdate = async (newDetails) => {
    try {
      setIsUpdatingColor(true);
      const userDocRef = doc(db, 'users', user.uid);
      
      // Log the banner data to verify it's included
      console.log('handleDetailsUpdate - Banner data:', newDetails.banner);
      
      // Create a local reference to prevent any race conditions
      const localUpdatedDetails = {
        displayName: newDetails.displayName,
        email: newDetails.email,
        phone: newDetails.phone, 
        bio: newDetails.bio,
        textColor: newDetails.textColor,
        textOpacity: newDetails.textOpacity,
        banner: newDetails.banner // Make sure banner is included
      };
      
      // Make a deep copy to ensure we don't have reference issues
      const updatedProfile = JSON.parse(JSON.stringify(localUpdatedDetails));
      
      console.log('Updating profile details with:', updatedProfile);
      
      // Prepare update object for Firestore - critical for banner persistence
      const updates = {
        'profile.displayName': updatedProfile.displayName,
        'profile.email': updatedProfile.email,
        'profile.phone': updatedProfile.phone,
        'profile.bio': updatedProfile.bio,
        'profile.textColor': updatedProfile.textColor,
        'profile.textOpacity': updatedProfile.textOpacity,
        'profile.banner': updatedProfile.banner
      };
      
      // Update Firestore with all the data including banner
      await updateDoc(userDocRef, updates);
      console.log('Successfully updated user document with banner data');
      
      // Update local state with our local reference including banner data
      setUserData(prev => ({
        ...prev,
        profile: { 
          ...prev.profile, 
          ...updatedProfile
        }
      }));
      
      // Also update preview color
      setPreviewTextColor(updatedProfile.textColor);
      
    } catch (error) {
      console.error('Error saving details:', error);
    } finally {
      // Reset the flag after a short delay to allow state updates to complete
      setTimeout(() => {
        setIsUpdatingColor(false);
      }, 500);
    }
  };

  // Handle color changes for live preview with guard against circular updates
  const handleColorChange = (color, opacity) => {
    console.log('Color change requested:', color, 'opacity:', opacity);
    
    // Guard against circular updates
    if (isUpdatingColor) {
      console.log('Skipping color update - already updating');
      return;
    }
    
    // Set flag to prevent multiple rapid updates
    setIsUpdatingColor(true);
    
    // Update the preview color
    setPreviewTextColor(color);
    
    // Reset the flag after a short delay
    setTimeout(() => {
      setIsUpdatingColor(false);
    }, 500);
  };

  // Create a merged userData object with the preview color
  const previewUserData = {
    ...userData,
    profile: {
      ...userData.profile,
      textColor: previewTextColor
    }
  };

  // Determine if we're showing the welcome page (when no section is selected)
  const showWelcomePage = activeSection === '' || !activeSection;

  return (
    <main className={styles.mainContent}>
      {showWelcomePage && (
        <div className={styles.sectionContent}>
          <WelcomePage 
            userData={userData} 
            setActiveSection={setActiveSection}
            isProUser={isProUser} // Pass isProUser prop to WelcomePage
          />
        </div>
      )}
      
      {activeSection === 'icons' && (
        <div className={styles.sectionContent}>
          <h1>Choose Your Icons</h1>
          <IconSelector 
            userId={user.uid}
            initialIcons={userData.profile?.icons || []}
            // For pro users, use the palette's darkest color; for free users, default to black.
            currentColor={isProUser ? getDarkestColor() : '#000000'}
            onSave={(icons) => {
              setUserData(prev => ({
                ...prev,
                profile: { ...prev.profile, icons }
              }));
            }}
            isProUser={isProUser}
            onUpgradeClick={onUpgradeClick}
          />
        </div>
      )}
      
      {activeSection === 'links' && (
        <div className={styles.sectionContent}>
          <h1>Manage Your Links</h1>
          <LinksManager
            userId={user.uid}
            initialLinks={userData.profile?.links || []}
            onSave={(links) => {
              setUserData(prev => ({
                ...prev,
                profile: { ...prev.profile, links }
              }));
            }}
            isProUser={isProUser}
            onUpgradeClick={onUpgradeClick}
          />
        </div>
      )}
      
      {activeSection === 'theme' && (
  <div className={styles.sectionContent}>
    <h1>Choose Your Theme</h1>
    <ThemeSelector
      userId={user.uid}
      initialTheme={userData.profile?.theme || 'classic'}
      onSave={(theme) => {
        setUserData(prev => ({
          ...prev,
          profile: { ...prev.profile, theme }
        }));
      }}
      userData={userData}
      isProUser={isProUser} // This is critical - ensure it's true for pro users
      onUpgradeClick={onUpgradeClick}
    />
  </div>
)}
      
      {activeSection === 'palette' && (
        <div className={styles.sectionContent}>
          <h1>Choose Your Palette</h1>
          <PaletteSelector
            userId={user.uid}
            initialPalette={userData.profile?.palette}
            setActiveSection={setActiveSection}
            onSave={(palette) => {
              setUserData(prev => ({
                ...prev,
                profile: { ...prev.profile, palette }
              }));
            }}
            isProUser={isProUser}
            onUpgradeClick={onUpgradeClick}
          />
        </div>
      )}
      
      {activeSection === 'details' && (
        <div className={styles.sectionContent}>
          <h1>Edit Your Details</h1>
          <DetailsEditor
            initialDetails={{
              displayName: userData.profile?.displayName || '',
              email: userData.profile?.email || '',
              phone: userData.profile?.phone || '',
              bio: userData.profile?.bio || '',
              textColor: userData.profile?.textColor || '#444444',
              textOpacity: userData.profile?.textOpacity || 70,
              banner: userData.profile?.banner || null // Include banner data
            }}
            onSave={handleDetailsUpdate}
            onColorChange={handleColorChange}
            userData={userData} // Pass userData for profile photo
            user={user} // Pass user for profile photo upload
          />
        </div>
      )}
      
      {activeSection === 'preview' && (
        <div className={styles.sectionContent}>
          <h1>Profile Preview</h1>
          <ProfilePreview 
            userData={activeSection === 'details' ? previewUserData : userData}
            setUserData={setUserData}
            getDarkestColor={getDarkestColor}
            isProUser={isProUser}
            onUpgradeClick={onUpgradeClick}
          />
        </div>
      )}

      {activeSection === 'ghost' && (
        <div className={styles.sectionContent}>
          <GhostMode 
            userId={user.uid}
            userData={userData}
            isProUser={isProUser}
            onUpgradeClick={onUpgradeClick}
          />
        </div>
      )}
      
      {activeSection === 'shortener' && (
        <div className={styles.sectionContent}>
          <LinkShortener 
            userId={user.uid}
            isProUser={isProUser}
            onUpgradeClick={onUpgradeClick}
          />
        </div>
      )}

      {/* New Subscription section */}
      {activeSection === 'subscription' && (
        <div className={styles.sectionContent}>
          <h1>Your Subscription</h1>
          <SubscriptionInfo 
            userId={user.uid}
            tierType={userData.tierType}
          />
        </div>
      )}

      {activeSection === 'cancelAccount' && (
        <div className={styles.sectionContent}>
          <h1>Manage Subscription</h1>
          <CancelAccount 
            userId={user.uid}
            tierType={userData.tierType}
          />
        </div>
      )}

      {activeSection === 'reportProblem' && (
        <div className={styles.sectionContent}>
          <ReportProblem 
            userData={userData}
          />
        </div>
      )}
    </main>
  );
};

export default DashboardContent;